/**
 * Document Picker Modal — select Google Docs or Notion pages to sync to Brain
 *
 * Usage:
 *   import { openDocPicker } from './ui/docPicker.js';
 *   openDocPicker('google_docs', { onComplete: () => refresh() });
 *   openDocPicker('notion', { onComplete: () => refresh() });
 */

import { client } from '../api/client.js';

let _overlay = null;

const MAX_SELECTION = 25;

export function openDocPicker(provider, opts = {}) {
    closeDocPicker();

    const isGoogle = provider === 'google_docs';
    const title = isGoogle ? 'Select Google Docs' : 'Select Notion Pages';
    const subtitle = isGoogle
        ? 'Choose documents or folders to sync into your Brain vault'
        : 'Choose pages to sync into your Brain vault';

    const overlay = document.createElement('div');
    overlay.className = 'dp-overlay';
    overlay.innerHTML = `
        <div class="dp-modal">
            <div class="dp-header">
                <div>
                    <div class="dp-title">${title}</div>
                    <div class="dp-subtitle">${subtitle}</div>
                </div>
                <button class="dp-close" id="dp-close">&times;</button>
            </div>
            <div class="dp-body">
                <div class="dp-status">Loading available ${isGoogle ? 'documents' : 'pages'}...</div>
                <div class="dp-list" id="dp-list"></div>
            </div>
            <div class="dp-footer">
                <span class="dp-count" id="dp-count">0 selected (max ${MAX_SELECTION})</span>
                <div class="dp-actions">
                    <button class="btn btn-secondary btn-sm" id="dp-cancel">Cancel</button>
                    <button class="btn btn-primary btn-sm" id="dp-sync" disabled>Sync Selected</button>
                </div>
            </div>
        </div>
    `;

    document.body.appendChild(overlay);
    _overlay = overlay;

    const selected = new Set();          // doc IDs
    const selectedFolders = new Set();   // folder IDs (for syncing whole folders)
    const expandedFolders = new Set();   // folder IDs currently expanded
    const folderDocs = {};               // folderId -> [doc, ...]
    const loadingFolders = new Set();    // folders currently loading

    const listEl = overlay.querySelector('#dp-list');
    const statusEl = overlay.querySelector('.dp-status');
    const countEl = overlay.querySelector('#dp-count');
    const syncBtn = overlay.querySelector('#dp-sync');

    // Close handlers
    overlay.querySelector('#dp-close').onclick = closeDocPicker;
    overlay.querySelector('#dp-cancel').onclick = closeDocPicker;
    overlay.addEventListener('click', (e) => { if (e.target === overlay) closeDocPicker(); });
    document.addEventListener('keydown', function _dpEsc(e) {
        if (e.key === 'Escape') { closeDocPicker(); document.removeEventListener('keydown', _dpEsc); }
    });

    const _esc = s => !s ? '' : String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');

    function updateCount() {
        countEl.textContent = `${selected.size} selected (max ${MAX_SELECTION})`;
        syncBtn.disabled = selected.size === 0 && selectedFolders.size === 0;
    }

    function updateSelectAllLink(items) {
        const link = listEl.querySelector('.dp-select-all');
        if (!link) return;
        const allDocIds = items.filter(i => i.type !== 'folder').map(i => i.id);
        const allSelected = allDocIds.length > 0 && allDocIds.every(id => selected.has(id));
        link.textContent = allSelected ? 'Deselect All' : 'Select All';
    }

    function renderFolderChildren(folderId) {
        const container = listEl.querySelector(`[data-folder-children="${folderId}"]`);
        if (!container) return;
        const docs = folderDocs[folderId] || [];
        if (docs.length === 0) {
            container.innerHTML = '<div class="dp-empty" style="padding-left:32px;font-size:12px;">No documents in this folder</div>';
            return;
        }
        container.innerHTML = docs.map(item => {
            const date = item.modifiedTime;
            const dateStr = date ? new Date(date).toLocaleDateString() : '';
            return `
                <label class="dp-item dp-item-child" data-id="${_esc(item.id)}" style="padding-left:32px;">
                    <input type="checkbox" class="dp-checkbox dp-doc-checkbox" value="${_esc(item.id)}" data-parent-folder="${_esc(folderId)}" ${selected.has(item.id) ? 'checked' : ''}>
                    <div class="dp-item-icon"><span style="font-size:14px;">&#x1F4C4;</span></div>
                    <div class="dp-item-info">
                        <div class="dp-item-name">${_esc(item.name)}</div>
                        ${dateStr ? `<div class="dp-item-date">Last modified: ${dateStr}</div>` : ''}
                    </div>
                </label>
            `;
        }).join('');
    }

    async function toggleFolder(folderId) {
        if (expandedFolders.has(folderId)) {
            // Collapse
            expandedFolders.delete(folderId);
            const container = listEl.querySelector(`[data-folder-children="${folderId}"]`);
            if (container) container.innerHTML = '';
            const caret = listEl.querySelector(`[data-caret="${folderId}"]`);
            if (caret) caret.style.transform = 'rotate(0deg)';
            return;
        }

        // Expand
        expandedFolders.add(folderId);
        const caret = listEl.querySelector(`[data-caret="${folderId}"]`);
        if (caret) caret.style.transform = 'rotate(90deg)';

        if (folderDocs[folderId]) {
            renderFolderChildren(folderId);
            return;
        }

        if (loadingFolders.has(folderId)) return;
        loadingFolders.add(folderId);

        const container = listEl.querySelector(`[data-folder-children="${folderId}"]`);
        if (container) container.innerHTML = '<div style="padding-left:32px;font-size:12px;color:#888;">Loading...</div>';

        try {
            const res = await client.googleDocsFolderContents(folderId);
            folderDocs[folderId] = res.docs || [];
            renderFolderChildren(folderId);
        } catch (err) {
            if (container) container.innerHTML = `<div style="padding-left:32px;font-size:12px;color:#e55;">Failed to load: ${_esc(err.message)}</div>`;
        } finally {
            loadingFolders.delete(folderId);
        }
    }

    function selectAllInFolder(folderId, checked) {
        const docs = folderDocs[folderId] || [];
        for (const doc of docs) {
            if (checked) {
                if (selected.size < MAX_SELECTION) selected.add(doc.id);
            } else {
                selected.delete(doc.id);
            }
        }
        // Update child checkboxes if visible
        const childCheckboxes = listEl.querySelectorAll(`input[data-parent-folder="${folderId}"]`);
        childCheckboxes.forEach(cb => { cb.checked = checked && selected.has(cb.value); });
        updateCount();
    }

    // Fetch available docs/pages
    const fetchFn = isGoogle ? client.googleDocsList : client.notionPages;
    fetchFn().then(res => {
        const items = isGoogle ? (res.docs || []) : (res.pages || []);
        statusEl.style.display = 'none';

        if (items.length === 0) {
            listEl.innerHTML = '<div class="dp-empty">No documents found. Make sure your account has accessible documents.</div>';
            return;
        }

        // Sort: folders first, then documents, each group by name
        const sorted = [...items].sort((a, b) => {
            const aIsFolder = a.type === 'folder' ? 0 : 1;
            const bIsFolder = b.type === 'folder' ? 0 : 1;
            if (aIsFolder !== bIsFolder) return aIsFolder - bIsFolder;
            return (a.name || '').localeCompare(b.name || '');
        });

        // Build HTML
        let html = '<div style="text-align:right;margin-bottom:6px;"><a href="#" class="dp-select-all" style="font-size:12px;color:var(--primary, #6C5CE7);cursor:pointer;">Select All</a></div>';
        html += sorted.map(item => {
            const isFolder = item.type === 'folder';
            const date = item.modifiedTime || item.lastEdited;
            const dateStr = date ? new Date(date).toLocaleDateString() : '';

            if (isFolder) {
                return `
                    <div class="dp-folder-group" data-folder-id="${_esc(item.id)}">
                        <label class="dp-item dp-item-folder" data-id="${_esc(item.id)}">
                            <input type="checkbox" class="dp-checkbox dp-folder-checkbox" value="${_esc(item.id)}">
                            <span class="dp-caret" data-caret="${_esc(item.id)}" style="display:inline-block;width:16px;text-align:center;cursor:pointer;transition:transform 0.15s;font-size:11px;color:#888;user-select:none;">&#x25B6;</span>
                            <div class="dp-item-icon"><span style="font-size:14px;">&#x1F4C1;</span></div>
                            <div class="dp-item-info">
                                <div class="dp-item-name">${_esc(item.name)}</div>
                                ${dateStr ? `<div class="dp-item-date">Last modified: ${dateStr}</div>` : ''}
                            </div>
                        </label>
                        <div class="dp-folder-children" data-folder-children="${_esc(item.id)}"></div>
                    </div>
                `;
            }

            return `
                <label class="dp-item" data-id="${_esc(item.id)}">
                    <input type="checkbox" class="dp-checkbox dp-doc-checkbox" value="${_esc(item.id)}">
                    <div class="dp-item-icon">${isGoogle
                        ? '<span style="font-size:14px;">&#x1F4C4;</span>'
                        : '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16v16H4z"/><path d="M9 9h6"/><path d="M9 13h6"/></svg>'
                    }</div>
                    <div class="dp-item-info">
                        <div class="dp-item-name">${_esc(item.name)}</div>
                        ${dateStr ? `<div class="dp-item-date">Last modified: ${dateStr}</div>` : ''}
                    </div>
                </label>
            `;
        }).join('');

        listEl.innerHTML = html;

        // Select All / Deselect All handler
        const selectAllLink = listEl.querySelector('.dp-select-all');
        if (selectAllLink) {
            selectAllLink.addEventListener('click', (e) => {
                e.preventDefault();
                const allDocIds = sorted.filter(i => i.type !== 'folder').map(i => i.id);
                const allSelected = allDocIds.length > 0 && allDocIds.every(id => selected.has(id));
                if (allSelected) {
                    // Deselect all top-level docs
                    allDocIds.forEach(id => selected.delete(id));
                } else {
                    // Select all top-level docs (up to max)
                    for (const id of allDocIds) {
                        if (selected.size >= MAX_SELECTION) break;
                        selected.add(id);
                    }
                }
                // Update checkboxes
                listEl.querySelectorAll('.dp-doc-checkbox:not([data-parent-folder])').forEach(cb => {
                    cb.checked = selected.has(cb.value);
                });
                updateCount();
                updateSelectAllLink(sorted);
            });
        }

        // Caret click handlers (expand/collapse folders)
        listEl.querySelectorAll('.dp-caret').forEach(caret => {
            caret.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const folderId = caret.dataset.caret;
                toggleFolder(folderId);
            });
        });

        // Also toggle folder when clicking the folder name area (but not the checkbox)
        listEl.querySelectorAll('.dp-item-folder .dp-item-info').forEach(info => {
            info.style.cursor = 'pointer';
            info.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const folderItem = info.closest('.dp-item-folder');
                const folderId = folderItem?.dataset?.id;
                if (folderId) toggleFolder(folderId);
            });
        });

        // Checkbox handlers
        listEl.addEventListener('change', (e) => {
            if (!e.target.classList.contains('dp-checkbox')) return;

            if (e.target.classList.contains('dp-folder-checkbox')) {
                const folderId = e.target.value;
                if (e.target.checked) {
                    selectedFolders.add(folderId);
                    // If folder docs are loaded, select them all
                    if (folderDocs[folderId]) {
                        selectAllInFolder(folderId, true);
                    } else {
                        // Load folder contents and then select all
                        toggleFolder(folderId).then(() => {
                            selectAllInFolder(folderId, true);
                        });
                    }
                } else {
                    selectedFolders.delete(folderId);
                    selectAllInFolder(folderId, false);
                }
                updateCount();
                return;
            }

            // Regular doc checkbox
            const id = e.target.value;
            if (e.target.checked) {
                if (selected.size >= MAX_SELECTION) { e.target.checked = false; return; }
                selected.add(id);
            } else {
                selected.delete(id);
                // If this doc is inside a folder, uncheck the folder checkbox
                const parentFolder = e.target.dataset.parentFolder;
                if (parentFolder) {
                    selectedFolders.delete(parentFolder);
                    const folderCb = listEl.querySelector(`.dp-folder-checkbox[value="${parentFolder}"]`);
                    if (folderCb) folderCb.checked = false;
                }
            }
            updateCount();
            updateSelectAllLink(sorted);
        });
    }).catch(err => {
        statusEl.textContent = `Failed to load: ${err.message}`;
    });

    // Sync handler
    syncBtn.onclick = async () => {
        if (selected.size === 0 && selectedFolders.size === 0) return;
        syncBtn.disabled = true;
        syncBtn.textContent = 'Syncing...';

        try {
            const docIds = [...selected];
            const folderIds = [...selectedFolders];
            if (isGoogle) {
                await client.googleDocsSync(docIds, folderIds);
            } else {
                await client.notionSync(docIds);
            }
            if (opts.onComplete) opts.onComplete();
            closeDocPicker();
        } catch (err) {
            syncBtn.textContent = 'Sync Failed \u2014 Retry';
            syncBtn.disabled = false;
            console.error('[DOC_PICKER_SYNC]', err);
        }
    };
}

export function closeDocPicker() {
    if (_overlay) { _overlay.remove(); _overlay = null; }
}
